import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import InternationalDataCard from "../../shared/component/InternationalDataCard";
import ViewToggle, { ViewMode } from "../../shared/component/ViewToggle";
import AlphabeticalCountryList from "../../shared/component/AlphabeticalCountryList";
import CountryDetailModal from "../../shared/component/CountryDetailModal";
import { DataItem } from "../../shared/definitions/internationalTypes";
import { groupByAlphabet } from "../../shared/utilities/alphabeticalGrouping";
import useQuerySearchOrganizationProfiles from "../hooks/useQuerySearchOrganizationProfiles";
import useQueryAllSearchOrganizationProfiles from "../hooks/useQueryAllSearchOrganizationProfiles";

interface OrganizationProfilesSearchProps {
  searchTerm: string;
  className?: string;
}

const OrganizationProfilesSearch: React.FC<OrganizationProfilesSearchProps> = ({
  searchTerm,
  className = "",
}) => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [selectedCountry, setSelectedCountry] = useState<{
    name: string;
    items: DataItem[];
  } | null>(null);

  // Use different hooks based on view mode
  const gridQuery = useQuerySearchOrganizationProfiles(searchTerm);
  const allQuery = useQueryAllSearchOrganizationProfiles(searchTerm);

  // Switch between queries based on view mode
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = viewMode === "alphabetical" ? allQuery : gridQuery;

  // Flatten all pages data
  const allResults = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.organisasi_internasional.data);
  }, [data]);

  // Group results by country/institution (lembaga) for grid view
  const groupedResults = useMemo(() => {
    const groups: Record<string, DataItem[]> = {};

    allResults.forEach((profile) => {
      const country = profile.lembaga;
      if (!groups[country]) {
        groups[country] = [];
      }
      groups[country].push(profile);
    });

    // Sort countries alphabetically
    const sortedCountries = Object.keys(groups).sort();
    const sortedGroups: Record<string, DataItem[]> = {};
    sortedCountries.forEach((country) => {
      sortedGroups[country] = groups[country];
    });

    return sortedGroups;
  }, [allResults]);

  // Group results alphabetically for alphabetical view
  const alphabeticalGroups = useMemo(() => {
    return groupByAlphabet(allResults);
  }, [allResults]);

  const handleViewProfile = (item: DataItem) => {
    navigate(`/tentang/organisasi-internasional/view/${item.id}`);
  };

  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const handleCountryClick = (country: string, items: DataItem[]) => {
    setSelectedCountry({ name: country, items });
  };

  const handleCloseModal = () => {
    setSelectedCountry(null);
  };

  // Don't show anything if no search term
  if (!searchTerm.trim()) {
    return null;
  }

  // Loading state
  if (isLoading) {
    return (
      <div className={`flex justify-center items-center py-12 ${className}`}>
        <div className="loading loading-spinner loading-lg text-success"></div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="alert alert-error max-w-md mx-auto">
          <svg
            className="w-12 h-12 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <p className="text-lg font-semibold">Gagal melakukan pencarian</p>
            <p className="text-sm opacity-70 mt-2">Silakan coba lagi nanti</p>
          </div>
        </div>
      </div>
    );
  }

  // Empty results
  if (Object.keys(groupedResults).length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="alert alert-warning max-w-md mx-auto">
          <svg
            className="w-12 h-12 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <div>
            <p className="text-lg font-semibold">Tidak ada hasil ditemukan</p>
            <p className="text-sm opacity-70 mt-2">
              Tidak ada organisasi yang cocok dengan &ldquo;{searchTerm}&rdquo;
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Search Results Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-2">
          Hasil Pencarian: &ldquo;{searchTerm}&rdquo;
        </h2>
        <div className="flex justify-between items-center">
          <p className="text-sm text-base-content/60">
            Ditemukan {allResults.length} organisasi dari{" "}
            {Object.keys(groupedResults).length} negara/institusi
            {viewMode === "alphabetical" &&
              ` • ${Object.keys(alphabeticalGroups).length} huruf`}
          </p>
          <ViewToggle currentView={viewMode} onViewChange={setViewMode} />
        </div>
      </div>

      {/* Content based on view mode */}
      {viewMode === "grid" ? (
        <>
          {/* Country Groups - Grid View */}
          {Object.entries(groupedResults).map(([country, profiles]) => (
            <div key={country} className="mb-8">
              {/* Country Header */}
              <div className="mb-6">
                <h3 className="text-xl font-bold mb-2">{country}</h3>
                <div className="h-1 w-16 bg-success rounded"></div>
                <p className="text-sm opacity-70 mt-2">
                  {profiles.length} dokumen ditemukan
                </p>
              </div>

              {/* Profiles Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6">
                {profiles.map((profile) => (
                  <InternationalDataCard
                    key={profile.id}
                    item={profile}
                    onView={handleViewProfile}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* Load More Button */}
          {hasNextPage && (
            <div className="text-center py-8">
              <button
                onClick={handleLoadMore}
                disabled={isFetchingNextPage}
                className="btn btn-success"
              >
                {isFetchingNextPage ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Memuat...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    Muat Lebih Banyak
                  </>
                )}
              </button>
            </div>
          )}
        </>
      ) : (
        /* Alphabetical View */
        <AlphabeticalCountryList
          alphabeticalData={alphabeticalGroups}
          onCountryClick={handleCountryClick}
          listType="organization"
        />
      )}

      {/* Country Detail Modal */}
      {selectedCountry && (
        <CountryDetailModal
          isOpen={!!selectedCountry}
          onClose={handleCloseModal}
          country={selectedCountry.name}
          items={selectedCountry.items}
          onViewDocument={handleViewProfile}
          listType="organization"
        />
      )}
    </div>
  );
};

export default OrganizationProfilesSearch;
